import {GoChevronRight} from 'react-icons/go';
import clsx from 'clsx';
import Link from 'next/link';
import type {BreadcrumbList, WithContext} from 'schema-dts';

import {Container} from '../container';
import {JsonLd} from '../jsonld';

const siteUrl = process.env.SITE_URL || '';

export type BreadcrumbItem = {
	title: string;
	href: string;
};

type Props = {
	breadcrumb: BreadcrumbItem[];
	theme?: 'light' | 'dark';
};

export const Breadcrumb = ({breadcrumb, theme = 'light'}: Props) => {
	const itemListELements: BreadcrumbList['itemListElement'] = breadcrumb.map((path, index) => {
		const isLast = index === breadcrumb.length - 1;
		return {
			'@type': 'ListItem',
			position: index + 2,
			name: path.title,
			// If this is the last item, we don't want to link it
			item: isLast ? undefined : `${siteUrl}${path.href}`,
		};
	});

	const jsonLd: WithContext<BreadcrumbList> = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Forsiden',
				item: `${siteUrl}/`,
			},
			...itemListELements,
		],
	};

	return (
		<Container as="nav" aria-label="Du er her">
			<JsonLd jsonLd={jsonLd} />
			<ol className={clsx('flex', 'gap-2', 'py-6', 'flex-wrap', 'text-sm')}>
				<li className={clsx('flex', 'items-center', 'gap-2')}>
					<Link
						href="/"
						className={clsx(theme === 'light' ? 'text-primary-15' : 'text-primary-80')}
					>
						Forsiden
					</Link>
					<GoChevronRight aria-hidden className={clsx('text-grey-25', 'text-base')} />
				</li>
				{breadcrumb.map((path) => {
					const isLast = path === breadcrumb[breadcrumb.length - 1];
					return (
						<li key={path.href} className={clsx('flex', 'items-center', 'gap-2')}>
							{isLast ? (
								<span
									aria-current="page"
									className={clsx(
										theme === 'light' ? 'text-grey-15' : 'text-secondary-45',
									)}
								>
									{path.title}
								</span>
							) : (
								<>
									<Link
										href={path.href}
										className={clsx(
											theme === 'light'
												? 'text-primary-15'
												: 'text-primary-80',
										)}
									>
										{path.title}
									</Link>
									<GoChevronRight
										aria-hidden
										className={clsx('text-grey-25', 'text-base')}
									/>
								</>
							)}
						</li>
					);
				})}
			</ol>
		</Container>
	);
};
